/**
 * Service Desk da TI (Onda 1, SD-1; ADR 0082): formulário único, filas, etapas do chamado e sigilo
 * da fila Segurança e LGPD. As mesmas regras valem na tela e no servidor; o banco confere de novo.
 */
import { z } from 'zod';
import { defineFormSchemas, validateFormFieldValues, type FormSchemaDef } from './forms.ts';
import { itAllocationChangeInput } from './it-allocation-change.ts';
import { localizedText } from './schemas.ts';

export const IT_TICKET_STATUSES = [
  'open',
  'in_triage',
  'in_progress',
  'awaiting_requester',
  'awaiting_approval',
  'resolved',
  'closed',
  'cancelled',
] as const;
export type ItTicketStatus = (typeof IT_TICKET_STATUSES)[number];
export const IT_TICKET_CLOSED: readonly ItTicketStatus[] = ['closed', 'cancelled'];

/** Recorte da Central de atendimento pelo prazo do SLA, vindo da Visão geral (ADR 0097). */
export const itDeskSlaFilter = z.enum(['in', 'out', 'none']);
export type ItDeskSlaFilter = z.infer<typeof itDeskSlaFilter>;

/** Categorias do formulário único (D-SD2), na ordem do catálogo. */
export const IT_TICKET_CATEGORIES = [
  'support',
  'equipment',
  'software',
  'installation',
  'network',
  'security',
  'privacy',
  'access',
  'other',
  // Alteração de rateio de contrato (ADR 0094, etapa 7): os dados vêm em allocationChange.
  'contract_allocation',
] as const;
export type ItTicketCategory = (typeof IT_TICKET_CATEGORIES)[number];
/** Categorias sigilosas: só podem apontar para a fila sigilosa. */
export const IT_TICKET_CONFIDENTIAL_CATEGORIES: readonly ItTicketCategory[] = [
  'security',
  'privacy',
];

export const IT_TICKET_KINDS = ['incident', 'request'] as const;
export const IT_TICKET_LEVELS = ['low', 'medium', 'high'] as const;
export type ItTicketLevel = (typeof IT_TICKET_LEVELS)[number];
export const IT_TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type ItTicketPriority = (typeof IT_TICKET_PRIORITIES)[number];
export const IT_TICKET_SECURITY_CLASSES = [
  'security_incident',
  'data_subject_request',
  'question',
] as const;
export const IT_TICKET_FORWARD_TARGETS = ['dpo', 'legal', 'board'] as const;

/** Tipo de item do chamado no SLA corporativo (migração 0225, com a função `it.sla_can_see`). */
export const IT_TICKET_SLA_RECORD_TYPE = 'it.ticket';

/**
 * Atividade do catálogo de SLA que mede o chamado (SD-2, ADR 0082, adendo SD-2): os códigos já cadastrados no
 * catálogo corporativo. Segurança e privacidade têm atividades próprias (fila sigilosa), acessos
 * também; as demais categorias seguem o tipo do serviço (incidente ou solicitação).
 */
export function itTicketSlaActivity(category: ItTicketCategory, kind: 'incident' | 'request') {
  if (category === 'security') return 'TI.SEGURANCA_LGPD.INCIDENTE';
  if (category === 'privacy') return 'TI.SEGURANCA_LGPD.TITULAR';
  if (category === 'access') return 'TI.ACESSOS.CONCESSAO';
  return kind === 'incident' ? 'TI.SERVICE_DESK.INCIDENTE' : 'TI.SERVICE_DESK.SOLICITACAO';
}

/** Prioridade = impacto × urgência (a mesma conta de it.ticket_priority, no banco). */
export function itTicketPriority(impact: ItTicketLevel, urgency: ItTicketLevel): ItTicketPriority {
  if (impact === 'high' && urgency === 'high') return 'urgent';
  if ((impact === 'high' && urgency === 'medium') || (impact === 'medium' && urgency === 'high')) {
    return 'high';
  }
  const lowest = [impact, urgency].includes('low');
  const highest = [impact, urgency].includes('high');
  return lowest && !highest ? 'low' : 'normal';
}

/** Tipos de campo dos formulários da TI: sem cadastros nem anexos (os anexos vão no chamado). */
export const IT_TICKET_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'select',
  'multi_select',
  'checkbox',
] as const;
const ticketForms = defineFormSchemas(IT_TICKET_FIELD_TYPES);
export const itTicketFormSchemaInput = ticketForms.schema;
export type ItTicketFormSchema = z.infer<typeof itTicketFormSchemaInput>;

export function validateItTicketFieldValues(
  schema: FormSchemaDef,
  values: Record<string, unknown>,
): { path: string; message: string }[] {
  return validateFormFieldValues(schema, values);
}

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/** Abrir: categoria, empresa e unidade, o pedido, a urgência e os campos da categoria. */
export const itTicketCreateInput = z.object({
  serviceId: z.uuid(),
  legalEntityId: z.uuid(),
  branchId: z.uuid(),
  title: text(150),
  description: text(10000),
  urgency: z.enum(IT_TICKET_LEVELS).default('medium'),
  fieldValues: z.record(z.string(), z.unknown()).default({}),
  /** A equipe pode registrar em nome de outra pessoa (fila comum). */
  requesterUserId: z.uuid().optional(),
  /** Só na categoria de alteração de rateio: contrato, valor ou percentual, origem e destino. */
  allocationChange: itAllocationChangeInput.optional(),
});

/** Triagem: fila, impacto e urgência, responsável e as ligações com equipamento e licença. */
export const itTicketTriageInput = z.object({
  queueId: z.uuid(),
  impact: z.enum(IT_TICKET_LEVELS),
  urgency: z.enum(IT_TICKET_LEVELS),
  assigneeUserId: optionalId,
  /** Patrimônio do equipamento e código da contratação (TIC-...), conferidos no servidor. */
  assetTag: optionalText(40),
  subscriptionCode: optionalText(20),
  securityClass: z.enum(IT_TICKET_SECURITY_CLASSES).nullable().optional(),
  note: optionalText(4000),
});

export const itTicketNoteInput = z.object({
  note: text(4000),
  /** Só a equipe: comentário interno, que quem pediu não vê. */
  internal: z.boolean().default(false),
});
export const itTicketResolveInput = z.object({ resolution: text(4000) });
export const itTicketReasonInput = z.object({ reason: text(2000) });
export const itTicketForwardInput = z.object({
  to: z.enum(IT_TICKET_FORWARD_TARGETS),
  reason: text(2000),
  legalReference: optionalText(60),
});

/** Fila: nome, responsável e substituto formais (P14), ativa ou não. */
export const itTicketQueueInput = z
  .object({
    name: localizedText,
    responsibleUserId: optionalId,
    substituteUserId: optionalId,
    isActive: z.boolean(),
  })
  .refine((v) => !v.substituteUserId || v.substituteUserId !== v.responsibleUserId, {
    path: ['substituteUserId'],
    message: 'O substituto precisa ser outra pessoa',
  });

/** Serviço do catálogo: nome, orientação, fila inicial, liberação e ordem. */
export const itTicketServiceInput = z.object({
  name: localizedText,
  description: z
    .object({ 'pt-BR': z.string().trim().max(1000), en: z.string().trim().max(1000) })
    .default({ 'pt-BR': '', en: '' }),
  queueId: z.uuid(),
  isReady: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int().min(1).max(999),
});

// ---------------------------------------------------------------------------
// SD-4 (ADR 0082, adendo SD-4): problemas, base de conhecimento e avaliação.
// ---------------------------------------------------------------------------

export const IT_PROBLEM_STATUSES = ['open', 'resolved', 'cancelled'] as const;
export type ItProblemStatus = (typeof IT_PROBLEM_STATUSES)[number];
export const IT_KB_STATUSES = ['draft', 'published', 'archived'] as const;
export type ItKbStatus = (typeof IT_KB_STATUSES)[number];

/** Número de exibição do problema e do artigo (PRB-000001, KB-000001). */
export const itProblemCode = (n: number) => `PRB-${String(n).padStart(6, '0')}`;
export const itKbCode = (n: number) => `KB-${String(n).padStart(6, '0')}`;

/** Problema: empresa, o que se repete e quem cuida; causa e soluções ao longo da análise. */
export const itProblemInput = z.object({
  legalEntityId: z.uuid(),
  title: text(200).refine((v) => v.length >= 3, 'Use pelo menos 3 letras'),
  description: text(4000),
  ownerUserId: optionalId,
  rootCause: optionalText(4000),
  workaround: optionalText(4000),
});
export const itProblemUpdateInput = itProblemInput.omit({ legalEntityId: true });
/** Resolver: a causa e a solução definitiva são obrigatórias. */
export const itProblemResolveInput = z.object({ rootCause: text(4000), solution: text(4000) });
export const itProblemLinkInput = z.object({
  protocol: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^TI-\d{4}-\d{6}$/, 'Use o protocolo do chamado, por exemplo TI-2026-000001'),
});

/** Artigo da base de conhecimento: categoria opcional (vale para todas quando vazia). */
export const itKbArticleInput = z.object({
  category: z.enum(IT_TICKET_CATEGORIES).nullable().optional(),
  title: text(200).refine((v) => v.length >= 3, 'Use pelo menos 3 letras'),
  summary: text(500),
  body: text(20000),
});

/** Avaliação de quem pediu, depois do encerramento: nota de 1 a 5 e comentário opcional. */
export const itTicketRatingInput = z.object({
  score: z.number().int().min(1).max(5),
  comment: optionalText(1000),
});

// ---------------------------------------------------------------------------
// SD-5 (ADR 0082, adendo SD-5): rotinas periódicas e manutenção preventiva básica.
// ---------------------------------------------------------------------------

export const IT_ROUTINE_KINDS = ['control', 'preventive'] as const;
export type ItRoutineKind = (typeof IT_ROUTINE_KINDS)[number];
export const IT_ROUTINE_CONTROL_TYPES = [
  'inventory',
  'access_review',
  'license_review',
  'lgpd_requests',
  'software_review',
  'checklist',
  'other',
] as const;
export const IT_ROUTINE_FREQUENCIES = ['monthly', 'quarterly', 'semiannual', 'annual'] as const;
export type ItRoutineFrequency = (typeof IT_ROUTINE_FREQUENCIES)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use uma data válida');

/**
 * Rotina de controle (com o tipo) ou plano preventivo (um equipamento pela etiqueta ou uma classe
 * de bens), com responsável, substituto, periodicidade e a próxima data.
 */
export const itRoutineInput = z
  .object({
    legalEntityId: z.uuid(),
    kind: z.enum(IT_ROUTINE_KINDS),
    controlType: z.enum(IT_ROUTINE_CONTROL_TYPES).nullable().optional(),
    title: text(200).refine((v) => v.length >= 3, 'Use pelo menos 3 letras'),
    instructions: text(4000),
    assetTag: optionalText(40),
    assetClassId: optionalId,
    responsibleUserId: z.uuid(),
    substituteUserId: optionalId,
    frequency: z.enum(IT_ROUTINE_FREQUENCIES),
    nextDueOn: isoDate,
    isActive: z.boolean().default(true),
    // G10 (0344): aviso antes do prazo e atraso levado à gestão de TI.
    remindDaysBefore: z.number().int().min(0).max(60).default(7),
    escalateDaysAfter: z.number().int().min(1).max(90).default(5),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'control' && !v.controlType) {
      ctx.addIssue({ code: 'custom', path: ['controlType'], message: 'Escolha o tipo da rotina' });
    }
    if (v.kind === 'preventive' && !v.assetTag === !v.assetClassId) {
      ctx.addIssue({
        code: 'custom',
        path: ['assetTag'],
        message: 'Informe um equipamento ou uma classe de bens (um dos dois)',
      });
    }
    if (v.substituteUserId && v.substituteUserId === v.responsibleUserId) {
      ctx.addIssue({
        code: 'custom',
        path: ['substituteUserId'],
        message: 'O substituto precisa ser outra pessoa',
      });
    }
  });

/** Execução da ocorrência pendente: feita (o que foi feito) ou não feita (o motivo). */
export const itRoutineRunInput = z.object({
  outcome: z.enum(['done', 'skipped']),
  note: text(4000),
});

// ---------------------------------------------------------------------------
// SD-3 (ADR 0082, adendo SD-3): aprovação de equipamento e de software pelo chamado.
// ---------------------------------------------------------------------------

export const IT_TICKET_APPROVAL_KINDS = ['equipment', 'software'] as const;
export type ItTicketApprovalKind = (typeof IT_TICKET_APPROVAL_KINDS)[number];
/** Categoria do chamado que pede cada aprovação. */
export const IT_TICKET_APPROVAL_BY_CATEGORY: Partial<
  Record<ItTicketCategory, ItTicketApprovalKind>
> = { equipment: 'equipment', software: 'software', installation: 'software' };

/**
 * Enviar para Compras (pedido do Fabricio, 03/11/2026): a TI analisou o chamado de equipamento ou
 * software e precisa comprar. A análise vale como aprovação da necessidade; a compra segue a alçada
 * na contratação. O servidor monta a solicitação e confere com as regras de Compras.
 */
export const itTicketPurchaseInput = z.object({
  requestType: z.enum(['MATERIAIS', 'BENS', 'SERVICO_SIMPLES', 'SERVICO_RECORRENTE']),
  description: text(500),
  quantity: text(30),
  unit: text(20),
  softwareId: z.uuid().nullable().optional(),
  masterCostCenterId: z.uuid(),
  costCenterId: z.uuid(),
  justification: text(4000),
  estimatedAmount: optionalText(30),
  estimatedCurrency: optionalText(3),
  recurrenceFrequency: z.enum(['weekly', 'monthly', 'quarterly', 'yearly']).nullable().optional(),
  recurrenceMonths: z.number().int().min(1).max(120).nullable().optional(),
  affectsCac: z.boolean().nullable().optional(),
});
export type ItTicketPurchaseInput = z.infer<typeof itTicketPurchaseInput>;

/** Pedido de aprovação: centro de custo de quem vai usar e, no software, o parecer técnico. */
export const itTicketApprovalInput = z.object({
  costCenterId: z.uuid(),
  technicalOpinion: optionalText(4000),
});
